/* ==========================================================================
   hov-pdp-mobile.js — mobile buy-box modules for the product page
   --------------------------------------------------------------------------
   Owns ONLY the elements emitted by snippets/hov-pdp-mobile-modules.liquid
   (countdown, offer card amounts, quantity stepper) plus one appended
   "% OFF" pill in the price row. The recommendation slider beneath the offer
   card is assets/hov-pdp-offer.js. It never touches #productSelect, the pack
   radios, .enj-product-price, the gallery or the sticky bar — those stay with
   the theme's own selectCallback (sections/latest-product-template.liquid:86).

   WHY A DOCUMENT-LEVEL LISTENER, AND WHY THAT IS THE WHOLE TRICK
   Tapping a pack fires this chain:
     1. change on input[name="option-0"]                           (target phase)
     2. layout/theme.liquid:415 — jQuery handler bound DIRECTLY on .swatch :radio;
        copies the value into .single-option-selector and triggers change
     3. Shopify.OptionSelectors resolves the variant, sets #productSelect.value,
        calls onVariantSelected
     4. selectCallback rewrites .enj-product-price / .enj-product-price-compare
     5. ...only now does the event bubble up to `document`
   Listening on `document` therefore runs AFTER every directly-bound handler,
   guaranteed by the capture/target/bubble order — no setTimeout race, and
   nothing in the theme has to be patched or re-wrapped.

   Deliberately NOT wrapping window.selectCallback: it is passed by reference
   into Shopify.OptionSelectors, so reassigning it later is never seen.

   LAYOUT-AGNOSTIC: this file never reads innerWidth and never sets `display`.
   Visibility is entirely the stylesheets' job (hov-pdp-mobile.css below 992px,
   hov-pdp-desktop.css from 992px), so the same modules serve both layouts.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__hovPdpM) return;          // survives a theme-editor section reload
  window.__hovPdpM = true;

  var $ = function (sel, root) { return (root || document).querySelector(sel); };

  /* ---- "Read more" clamps ------------------------------------------------
     Placed BEFORE the variants guard below on purpose — neither has anything to
     do with the buy box, and both must still run on a product whose variant data
     is missing.

     Two clamps, one mechanism:
       - the buy-box short description: [data-hov-readmore="2"], the wrapper
         snippets/latest-product-extended.liquid puts around the `text` block;
       - the Description TAB: 3 lines. That section is not ours to add markup to,
         so its text children are moved into a new wrapper here, and pictures are
         left where they are so a clamp can never crop an image.

     The toggle is revealed only when the text genuinely overflows its line
     budget — a short description gets no button at all. The clamp itself is CSS
     (hov-pdp-mobile.css §8 below 992px, hov-pdp-desktop.css D5 from 992px). The
     wider desktop column allows two more lines than the budget named here. */
  (function readMore() {
    var CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M6 9l6 6 6-6"/></svg>';
    var uid = 0;
    var desk = window.matchMedia('(min-width: 992px)');

    function clamp(wrap, lines) {
      if (!wrap || wrap.getAttribute('data-hov-rm')) return;
      wrap.setAttribute('data-hov-rm', '1');
      wrap.classList.add('hov-readmore');
      if (!wrap.id) wrap.id = 'hov-readmore-' + (++uid);

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hov-readmore__btn';
      btn.setAttribute('aria-controls', wrap.id);
      btn.setAttribute('aria-expanded', 'false');
      btn.hidden = true;
      btn.innerHTML = '<span>Read more</span>' + CHEVRON;
      wrap.parentNode.insertBefore(btn, wrap.nextSibling);

      /* REAL line-heights, read from the first element that actually holds text
         (the buy-box wrapper starts with an EMPTY <p>), so the clamp is right
         whatever font size the theme or the section settings produce. */
      function measure() {
        if (!wrap.offsetParent) return;              // hidden tab: nothing to measure
        var probe = null, cands = wrap.querySelectorAll('p, li, span, div');
        for (var i = 0; i < cands.length; i++) {
          if ((cands[i].textContent || '').trim()) { probe = cands[i]; break; }
        }
        var cs = window.getComputedStyle(probe || wrap);
        var lh = parseFloat(cs.lineHeight);
        if (isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.5;   // "normal"
        var clampH = Math.ceil(lh * (desk.matches ? lines + 2 : lines));
        wrap.style.setProperty('--hov-clamp-h', clampH + 'px');
        var needs = wrap.scrollHeight > clampH + 4;
        wrap.classList.toggle('is-clamped', needs);
        btn.hidden = !needs;
        if (!needs) { wrap.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); }
      }

      btn.addEventListener('click', function () {
        var open = !wrap.classList.contains('is-open');
        if (open) {
          // Animate from the clamp to the real height, then release the limit so
          // a later reflow (fonts, rotation) can never re-crop the open text.
          wrap.style.maxHeight = wrap.scrollHeight + 'px';
          wrap.classList.add('is-open');
          window.setTimeout(function () { wrap.style.maxHeight = ''; }, 420);
        } else {
          wrap.style.maxHeight = wrap.scrollHeight + 'px';
          void wrap.offsetHeight;                    // commit the start height
          wrap.style.maxHeight = '';
          wrap.classList.remove('is-open');
        }
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.firstChild.textContent = open ? 'Read less' : 'Read more';
      });

      measure();
      window.addEventListener('load', measure);
      var t = null;
      window.addEventListener('resize', function () {
        if (t) window.clearTimeout(t);
        t = window.setTimeout(measure, 150);
      }, { passive: true });
      // Tabs and accordions hide their panels; re-measure when one opens. The
      // second pass covers Bootstrap's fade (150ms) and collapse (350ms).
      document.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest(
              '.tabs-ctm .tab-link, .tab-pd-details [data-toggle="tab"], #accordion [data-toggle="collapse"]')) {
          window.setTimeout(measure, 0);
          window.setTimeout(measure, 400);
        }
      });
    }

    // 1. Buy-box short description — the wrapper and its line budget come from Liquid.
    var marked = document.querySelectorAll('[data-hov-readmore]');
    for (var m = 0; m < marked.length; m++) {
      var hasText = (marked[m].textContent || '').trim();
      if (hasText) clamp(marked[m], parseInt(marked[m].getAttribute('data-hov-readmore'), 10) || 2);
    }

    // 2. Description tabs — 3 lines, wrapper built here (neither section is ours
    //    to add markup to). Latest template: the fresh-0 tab section. Default
    //    template: the Bootstrap #des pane, which only renders when the theme's
    //    description tab setting is on.
    [['.tabs-ctm .tab-content#Description', 'hov-readmore-description'],
     ['.tab-pd-details #des > .product-desc', 'hov-readmore-des']].forEach(function (t) {
      var tc = document.querySelector(t[0]);
      if (!tc || tc.querySelector('.hov-readmore')) return;
      var textNodes = Array.prototype.filter.call(tc.childNodes, function (n) {
        return !(n.nodeType === 1 && /^(PICTURE|IMG|VIDEO|IFRAME|SCRIPT|STYLE)$/.test(n.tagName));
      });
      var anyText = textNodes.some(function (n) { return (n.textContent || '').trim(); });
      if (!anyText) return;
      var wrap = document.createElement('div');
      wrap.id = t[1];
      tc.insertBefore(wrap, textNodes[0]);
      textNodes.forEach(function (n) { wrap.appendChild(n); });
      clamp(wrap, 3);
    });
  })();

  /* ---- thumbnail rail follows the current slide --------------------------
     The rail is vertical by CSS only (hov-pdp-mobile.css §14), so slick's own
     scrolling is off there and it scrolls natively. hov-pdp-desktop.js does the
     same at 992px and up. Before the variants guard: a gallery needs no buy box. */
  if (window.jQuery) jQuery('.gallery-control .js_prod_main2').on('afterChange', function (e, slick, index) {
    if (window.matchMedia('(min-width: 992px)').matches) return;
    var list = $('.gallery-control .js_prod_sub2 .slick-list');
    var thumb = list && $('.slick-slide[data-slick-index="' + index + '"]', list);
    if (!thumb) return;
    list.scrollTo({ top: Math.max(0, thumb.offsetTop - (list.clientHeight - thumb.offsetHeight) / 2), behavior: 'smooth' });
  });

  /* ---- comment carousel ---------------------------------------------------
     Up to three customer comments under the gallery, phones and tablets only
     (hov-pdp-mobile.css §15). It is a native snap scroller, so a finger always
     works; this only adds the advance every 4 seconds. It rests while the strip
     is off screen, under a finger or showing an expanded comment, and never runs
     for shoppers who asked for reduced motion. */
  (function commentCarousel() {
    var track = $('[data-hov-reviews]');
    if (!track || track.children.length < 2) return;
    var INTERVAL_MS = 4000;
    var cards = Array.prototype.slice.call(track.children);
    var dots = $('[data-hov-reviews-dots]');
    var small = window.matchMedia('(max-width: 991.98px)');
    var calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    var index = 0, timer = null, onScreen = false, held = false;

    if (dots) dots.innerHTML = cards.map(function (c, i) {
      return '<i' + (i ? '' : ' class="is-active"') + '></i>';
    }).join('');

    function advance() {
      if (held || document.hidden || $('.ctm-review-text.expanded', track)) return;
      var card = cards[(index + 1) % cards.length];
      track.scrollTo({ left: card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2, behavior: 'smooth' });
    }

    // Restarted after a swipe, so the shopper gets a full 4 seconds on their card.
    function run() {
      if (timer) { window.clearInterval(timer); timer = null; }
      if (onScreen && small.matches && !calm.matches) timer = window.setInterval(advance, INTERVAL_MS);
    }

    new IntersectionObserver(function (entries) {
      onScreen = entries[0].isIntersecting;
      run();
    }).observe(track);

    var inView = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        index = cards.indexOf(entry.target);
        if (dots) Array.prototype.forEach.call(dots.children, function (dot, i) {
          dot.classList.toggle('is-active', i === index);
        });
      });
    }, { root: track, threshold: 0.75 });
    cards.forEach(function (card) { inView.observe(card); });

    track.addEventListener('touchstart', function () { held = true; }, { passive: true });
    ['touchend', 'touchcancel'].forEach(function (name) {
      track.addEventListener(name, function () { held = false; run(); }, { passive: true });
    });
  })();

  /* ---- blank section headings --------------------------------------------
     Some sections render an <h2> holding only whitespace, which still carries the
     theme's bottom margin above the content. Tag those so the stylesheet can drop
     them; a heading with real text is never touched. Runs before the variants
     guard because it has nothing to do with the buy box. */
  Array.prototype.forEach.call(
    document.querySelectorAll('.full-secton .text-info h2.title.inline-richtext'),
    function (h) { if (!(h.textContent || '').trim()) h.classList.add('hov-blank'); });

  /* ---- money ------------------------------------------------------------
     Same call selectCallback:122 uses, so our amounts and the theme's are
     formatted identically. window.money_format is set at theme.liquid:231. */
  function money(cents) {
    var fmt = window.money_format || '₹ {{amount}}';
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(cents, fmt); } catch (e) { /* fall through */ }
    }
    return '₹ ' + (cents / 100).toFixed(2);
  }

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  var variants = readJSON($('[data-hov-pdpm-variants]')) || [];
  if (!variants.length) return;

  /* Coupons come from the catalog snippets/hov-coupon-catalog.liquid already
     emits (layout/theme.liquid:384). It is filtered server-side per visitor, so
     nothing private can be in it. Never build a second coupon source. */
  var coupon = (function () {
    var all = readJSON($('#hov-coupon-catalog'));
    if (!Array.isArray(all)) return null;
    var now = Date.now();
    var usable = all.filter(function (c) {
      if (!c || c.private || !c.code) return false;
      if (c.endsAt) { var t = Date.parse(c.endsAt); if (!isNaN(t) && t < now) return false; }
      return c.type === 'percentage' || c.type === 'fixed_amount';
    });
    usable.sort(function (a, b) { return (a.sort || 0) - (b.sort || 0); });
    return usable[0] || null;
  })();

  function qty() {
    var sel = document.getElementById('Quantity');
    var n = sel ? parseInt(sel.value, 10) : 1;
    return (isNaN(n) || n < 1) ? 1 : n;
  }

  /* The master select is the source of truth once OptionSelectors has written
     to it; the checked radio is the fallback before that happens. */
  function currentVariant() {
    var sel = document.getElementById('productSelect');
    var id = sel && parseInt(sel.value, 10);
    var found = id && variants.filter(function (v) { return v.id === id; })[0];
    if (found) return found;
    var checked = $('.maxus-productdetail__options input[type="radio"]:checked');
    var card = checked && checked.closest('.maxus-color');
    var cid = card && parseInt(card.getAttribute('data-variant-id'), 10);
    return variants.filter(function (v) { return v.id === cid; })[0] || variants[0];
  }

  var show = function (el) { if (el) el.removeAttribute('hidden'); };
  var hide = function (el) { if (el) el.setAttribute('hidden', ''); };

  /* ---- availability -----------------------------------------------------
     latest-product-extended.liquid never assigns `sold_out`, so a sold-out
     product still renders its offer modules. A countdown, a saving and a coupon
     on something that cannot be bought read as broken, so they are suppressed
     while the selected variant is unavailable, and the stepper goes with them.
     The trust panel stays: it is still true. */
  var soldOut = false;
  function renderAvailability(v) {
    soldOut = v.available === false;
    var form = document.getElementById('AddToCartForm');
    if (form) form.classList.toggle('hov-pdpm-soldout', soldOut);
    var q = $('[data-hov-qty]');
    if (q) (soldOut ? hide : show)(q);
  }
  // Seed now, before the countdown below starts, so a sold-out product never
  // flashes a timer for its first second.
  renderAvailability(currentVariant());

  /* ---- the lead "% OFF" pill -------------------------------------------
     selectCallback only ever sets .innerHTML on .enj-product-price, so a pill
     appended as its SIBLING survives every variant change. We create it once
     and then only update its text. */
  function renderOffPill(v) {
    // Latest template: price inside the buy box. Default template: price row
    // above the form. Never the sticky bar's copy, which sits outside both.
    var ins = $('#shoop_detail .enj-product-price') ||
              $('#content .detail-info > .relative > .product-price > ins.enj-product-price');
    if (!ins || !ins.parentNode) return;
    var pill = $('.hov-pdpm__off', ins.parentNode);
    var off = (v.mrp > v.price) ? Math.round((v.mrp - v.price) * 100 / v.mrp) : 0;
    if (!off) { if (pill) pill.remove(); return; }
    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'hov-pdpm__off';
      ins.parentNode.insertBefore(pill, ins.nextSibling);
    }
    pill.textContent = off + '% OFF';
  }

  function renderSaving(v) {
    var row = $('[data-hov-saving]');
    if (!row) return;
    if (soldOut) { hide(row); return; }
    var saving = (v.mrp > v.price) ? (v.mrp - v.price) * qty() : 0;
    if (saving <= 0) { hide(row); return; }
    var amt = $('[data-hov-saving-amount]', row);
    if (amt) amt.textContent = money(saving);
    show(row);
  }

  /* ---- cart the coupon is measured against -------------------------------
     Printed by the offer card, then kept current from every drawer rebuild.
     cartPreview is the value of slider taps Shopify has not confirmed yet, so the
     card answers in the same frame as the tap. */
  var offer = $('[data-hov-offer]');
  var cartValue = offer ? parseInt(offer.getAttribute('data-cart-value'), 10) || 0 : 0;
  var cartPreview = 0;
  var cartLines = {};                       // variant id -> true
  if (offer) (offer.getAttribute('data-cart-lines') || '').split(',').forEach(function (line) {
    if (line) cartLines[line.split(':')[0]] = true;
  });

  /* ---- coupon pane ------------------------------------------------------
     UNIT TRAP: the catalog stores `value` and `minSubtotal` in RUPEES, while
     variant prices are in PAISE. Multiply by 100 at this boundary or every
     figure is 100x wrong.

     minSubtotal is a CART threshold, so progress is the cart plus this selection.
     A variant already in the cart is already in cartValue.

     No rupee "you pay" figure: the live code and the card's percentage disagree
     (see the WELLNESS25 app in apps/hov-mrp-discount), so the amount could only be
     wrong at checkout. The cart drawer makes the same promise-free choice. */
  function renderTicket(v) {
    var row = $('[data-hov-ticket]');
    if (!row) return;
    var gap = 0, label = '';
    var copy = $('[data-hov-ticket-copy]', row);
    if (coupon && copy && !soldOut) {
      var have = cartValue + cartPreview + (cartLines[v.id] ? 0 : v.price * qty());
      var minCents = Math.round((coupon.minSubtotal || 0) * 100);
      var chip = '<b class="hov-pdpm__code" data-coupon-copy="' + coupon.code + '">' + coupon.code + '</b>';
      label = coupon.type === 'percentage'
        ? coupon.value + '% off'
        : money(Math.round(coupon.value * 100)) + ' off';
      gap = Math.max(minCents - have, 0);

      copy.innerHTML = gap
        ? 'Add <b>' + money(gap) + '</b> more to get ' + label + ' with ' + chip
        : label + ' unlocked. Use ' + chip + ' at checkout';
      var bar = $('[data-hov-ticket-bar]', row);
      var filled = minCents ? Math.min(have / minCents, 1) : 1;
      if (bar) bar.style.transform = 'translateX(' + ((filled - 1) * 100).toFixed(1) + '%)';
      row.classList.toggle('is-unlocked', !gap);
      show(row);
    } else {
      hide(row);
    }
    if (offer) {
      offer.setAttribute('data-gap', gap);
      offer.setAttribute('data-coupon-label', label);
      document.dispatchEvent(new CustomEvent('hov:offer:change'));
    }
  }

  // Both panes share one card, which closes up to one pane or disappears with the last.
  function renderOffer() {
    if (!offer) return;
    var panes = offer.querySelectorAll('[data-hov-saving]:not([hidden]), [data-hov-ticket]:not([hidden])').length;
    (panes ? show : hide)(offer);
    offer.classList.toggle('is-single', panes === 1);
  }

  /* ---- pack cards -------------------------------------------------------
     Only mirrors the checked state onto the card, so CSS can style the whole
     card rather than just the label. The native radio stays authoritative. */
  function renderPacks() {
    var cards = document.querySelectorAll('.hov-packs .maxus-color');
    for (var i = 0; i < cards.length; i++) {
      var input = cards[i].querySelector('input[type="radio"]');
      cards[i].classList.toggle('is-selected', !!(input && input.checked));
    }
  }

  function renderQty() {
    var val = $('[data-hov-qty-val]');
    if (val) val.textContent = String(qty());
  }

  function render() {
    var v = currentVariant();
    if (!v) return;
    renderAvailability(v);
    renderOffPill(v);
    renderSaving(v);
    renderTicket(v);
    renderOffer();
    renderPacks();
    renderQty();
  }

  /* ---- quantity stepper -------------------------------------------------
     Writes through to the existing <select id="Quantity"> and clamps to the
     options it actually offers, so it can never select a quantity the theme's
     own quantity_rule does not allow. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-hov-qty-step]');
    if (!btn) return;
    e.preventDefault();
    var sel = document.getElementById('Quantity');
    if (!sel) return;
    var step = parseInt(btn.getAttribute('data-hov-qty-step'), 10) || 0;
    var want = qty() + step;

    if (sel.tagName === 'SELECT') {
      var allowed = Array.prototype.map.call(sel.options, function (o) {
        return parseInt(o.value, 10);
      }).filter(function (n) { return !isNaN(n); });
      if (allowed.indexOf(want) === -1) return;          // outside the rule — ignore
      sel.value = String(want);
    } else {
      // Default template: ajax-cart swaps the number input for a text input
      // with min only, after this file runs — so it is looked up on every
      // click, never cached, and max is honoured only if a rule sets one.
      var min = parseInt(sel.getAttribute('min'), 10);
      if (isNaN(min) || min < 1) min = 1;
      var max = parseInt(sel.getAttribute('max'), 10);
      if (want < min || (!isNaN(max) && want > max)) return;
      sel.value = String(want);
    }
    // Let every existing listener see this exactly as a shopper-driven change.
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    render();
  }, false);

  /* ---- re-render triggers ----------------------------------------------- */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t) return;
    if (t.id === 'productSelect' || t.id === 'Quantity' ||
        (t.matches && t.matches('.maxus-productdetail__options input[type="radio"]'))) {
      render();
    }
  }, false);

  // ajax-cart.js and quickview.js both fire this after every rebuild, with the /cart.js payload.
  if (window.jQuery) jQuery(document.body).on('ajaxCart.afterCartLoad', function (e, cart) {
    if (!cart || !cart.items) return;
    cartValue = cart.original_total_price || 0;
    cartPreview = 0;
    cartLines = {};
    cart.items.forEach(function (item) { cartLines[item.variant_id] = true; });
    render();
  });

  document.addEventListener('hov:offer:preview', function (e) {
    cartPreview = e.detail.cents;
    render();
  });

  /* ---- countdown --------------------------------------------------------
     Fails closed: ships hidden and is only ever revealed for a deadline that is
     genuinely in the future, so a frozen 00:00 cannot appear. The deadline is
     stamped once per product in sessionStorage, so navigating away and back
     does not hand out a fresh clock.

     sessionStorage THROWS (not returns null) in some privacy modes, so the read
     and the write are guarded separately — a failed write must not prevent the
     timer from running for this pageview. */
  (function countdown() {
    var el = $('[data-hov-timer]');
    if (!el) return;
    var mins = parseInt(el.getAttribute('data-minutes'), 10) || 10;
    var key = 'hov-pdp-offer-' + el.getAttribute('data-product');
    var deadline = 0;

    try { deadline = parseInt(sessionStorage.getItem(key), 10) || 0; } catch (e) {}
    if (!deadline || deadline <= Date.now()) {
      deadline = Date.now() + mins * 60000;
      try { sessionStorage.setItem(key, String(deadline)); } catch (e) {}
    }

    var mm = $('[data-hov-timer-mm]', el),
        ss = $('[data-hov-timer-ss]', el),
        sr = $('[data-hov-timer-sr]', el),
        lastMin = -1, id = null;

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function tick() {
      var left = deadline - Date.now();
      if (left <= 0) {                      // hide, never freeze at 00:00
        hide(el);
        if (id) { clearInterval(id); id = null; }
        return;
      }
      if (soldOut) { hide(el); return; }     // nothing to hurry for
      var total = Math.floor(left / 1000);
      var m = Math.floor(total / 60), s = total % 60;
      if (mm) mm.textContent = pad(m);
      if (ss) ss.textContent = pad(s);
      if (sr && m !== lastMin) {            // announce per minute, not per second
        lastMin = m;
        sr.textContent = m + ' minutes left';
      }
      show(el);
    }

    tick();
    id = setInterval(tick, 1000);

    // A backgrounded tab throttles timers; resync on return rather than drift.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) tick();
    });
  })();

  /* ---- sold-out packs cannot be selected ---------------------------------
     Respect Shopify's real stock state at every width (the desktop pack cards in
     hov-pdp-desktop.css D7.4 dim sold-out packs exactly as mobile does).

     Why CSS alone was not enough: `pointer-events: none` stops a finger, but not
     a keyboard, assistive tech or a scripted label.click() — and a selected
     sold-out pack disables Add to cart (selectCallback:116), so it is a dead end.

     Why the theme could not do it: snippets/swatch.liquid emits, once per value,
         .removeClass('soldout').addClass('available').find(':radio').removeAttr('disabled')
     for EVERY value unconditionally. Those inline scripts run during parsing,
     before this deferred file, so setting `disabled` here wins. */
  (function soldOutGuard() {
    function apply() {
      var cards = document.querySelectorAll('.hov-packs .maxus-color[data-available="false"]');
      for (var i = 0; i < cards.length; i++) {
        var input = cards[i].querySelector('input[type="radio"]');
        if (input && !input.disabled) { input.disabled = true; input.setAttribute('data-hov-disabled', ''); }
      }
    }
    apply();
    window.addEventListener('load', apply);
  })();

  /* ---- sticky add-to-cart gate -----------------------------------------
     snippets/custom-js.liquid reveals the bar with:
         var section = $('.btn-action.detail-attr.qty-cart');
         if (scrollTop >= section.offset().top - 10) addClass('active')
     assets/hov-pdp-mobile.css sets that element to `display: contents` so its
     children can join the buy-box grid — and a display:contents element has no
     box, so .offset().top reads 0 and the condition is true from the first
     pixel. The bar was therefore always up, covering the real buy row.

     Rather than edit that shared script, the real decision is made here and
     published as a class on <html>; the stylesheet requires BOTH that class and
     the theme's own `.active` before the bar is visible. Desktop is untouched
     because only the mobile block reads the class.

     A scroll/resize check rather than an IntersectionObserver: anchor jumps and
     restored scroll positions produce no intersection events, so an observer can
     leave the bar in the wrong state after a back-navigation. */
  (function stickyGate() {
    var root = document.documentElement;
    var ticking = false;

    function evaluate() {
      ticking = false;
      var btn = $('#AddToCartForm .addto_caart .shop-button.enj-add-to-cart-btn') ||
                $('#AddToCartForm .btn-action.detail-attr.qty-cart > .shop-button.enj-add-to-cart-btn');
      if (!btn) return;
      // Up only once the real Add to cart has scrolled off the top.
      var passed = btn.getBoundingClientRect().bottom < 0;
      root.classList.toggle('hov-pdpm-sticky-up', passed);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(evaluate);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    evaluate();
    window.addEventListener('load', evaluate);
  })();

  /* OptionSelectors fires onVariantSelected once during its own construction,
     before this listener could exist — so seed from the DOM, then again on the
     next tick to pick up whatever that construction settled on. */
  render();
  setTimeout(render, 0);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  }
  window.addEventListener('load', render);
})();
